import React from 'react'

export function renderMarkdownDescription(content: string, className = 'text-xs text-[#ccc] leading-tight'): React.ReactNode {
  if (!content) return null

  const lines = content.split('\n')
  const elements: React.ReactNode[] = []
  let currentParagraph: string[] = []
  let inCodeBlock = false
  let codeBlockLines: string[] = []

  const flushParagraph = () => {
    if (currentParagraph.length > 0) {
      const text = currentParagraph.join(' ')
      elements.push(
        <span key={elements.length}>
          {parseInlineMarkdown(text)}
        </span>,
      )
      currentParagraph = []
    }
  }

  const flushCodeBlock = () => {
    if (codeBlockLines.length > 0) {
      const code = codeBlockLines.join('\n')
      elements.push(
        <pre key={elements.length} className="bg-[#0a0a0a] border border-[#333] rounded p-2 overflow-x-auto my-1.5">
          <code className="text-xs text-[#aaa] font-mono whitespace-pre">{code}</code>
        </pre>,
      )
      codeBlockLines = []
      inCodeBlock = false
    }
  }

  for (const line of lines) {
    const trimmed = line.trim()

    if (trimmed.startsWith('```')) {
      if (inCodeBlock) {
        flushCodeBlock()
      }
      else {
        flushParagraph()
        inCodeBlock = true
      }
    }
    else if (inCodeBlock) {
      codeBlockLines.push(line)
    }
    else if (trimmed === '') {
      flushParagraph()
      if (elements.length > 0) {
        elements.push(<br key={elements.length} />)
      }
    }
    else {
      currentParagraph.push(trimmed)
    }
  }

  flushParagraph()
  flushCodeBlock()

  return <div className={className}>{elements}</div>
}

function parseInlineMarkdown(text: string): React.ReactNode[] {
  const parts: React.ReactNode[] = []
  let currentIndex = 0

  const patterns = [
    { regex: /\[(.+?)\]\((.+?)\)/g,
      render: (match: string, text: string, url: string) => (
        <a key={currentIndex++} href={url} target="_blank" rel="noopener noreferrer" className="text-lime-300 hover:text-lime-200 underline">
          {text}
        </a>
      ) },
    { regex: /\*\*(.+?)\*\*/g,
      render: (match: string, content: string) => <strong key={currentIndex++}>{content}</strong> },
    { regex: /`(.+?)`/g,
      render: (match: string, content: string) => (
        <code key={currentIndex++} className="bg-[#2a2a2a] px-1 py-0.5 rounded text-sm">{content}</code>
      ) },
  ]

  let lastIndex = 0
  const matches: Array<{ index: number; length: number; render: () => React.ReactNode }> = []

  for (const pattern of patterns) {
    let match
    const regex = new RegExp(pattern.regex.source, pattern.regex.flags)
    while ((match = regex.exec(text)) !== null) {
      const matchIndex = match.index
      const matchLength = match[0].length
      const fullMatch = match[0]
      const args = match.slice(1)
      matches.push({
        index: matchIndex,
        length: matchLength,
        render: () => pattern.render(fullMatch, ...args),
      })
    }
  }

  matches.sort((a, b) => a.index - b.index)

  for (const match of matches) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index))
    }
    parts.push(match.render())
    lastIndex = match.index + match.length
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex))
  }

  return parts.length > 0 ? parts : [text]
}

export function renderMarkdownProse(content: string): React.ReactNode {
  if (!content) return null

  const lines = content.split('\n')
  const elements: React.ReactNode[] = []
  let currentParagraph: string[] = []
  let inList = false
  let listItems: string[] = []
  let inCodeBlock = false
  let codeBlockLines: string[] = []
  let codeBlockLanguage = ''

  const flushParagraph = () => {
    if (currentParagraph.length > 0) {
      const text = currentParagraph.join(' ')
      elements.push(
        <p key={elements.length} className="text-[#e0e0e0] leading-relaxed mb-4">
          {parseInlineMarkdown(text)}
        </p>,
      )
      currentParagraph = []
    }
  }

  const flushList = () => {
    if (listItems.length > 0) {
      elements.push(
        <ul key={elements.length} className="text-[#e0e0e0] leading-relaxed space-y-2 list-disc pl-10 mb-6">
          {listItems.map((item, i) => <li key={i}>{parseInlineMarkdown(item.trim())}</li>)}
        </ul>,
      )
      listItems = []
      inList = false
    }
  }

  const flushCodeBlock = () => {
    if (codeBlockLines.length > 0) {
      const code = codeBlockLines.join('\n')
      elements.push(
        <pre key={elements.length} className="bg-[#1a1a1a] border border-[#444] rounded p-3 overflow-x-auto mb-4">
          <code className="text-sm text-[#aaa] font-mono whitespace-pre">{code}</code>
        </pre>,
      )
      codeBlockLines = []
      codeBlockLanguage = ''
      inCodeBlock = false
    }
  }

  for (const line of lines) {
    const trimmed = line.trim()

    if (trimmed.startsWith('```')) {
      if (inCodeBlock) {
        flushCodeBlock()
      }
      else {
        flushParagraph()
        flushList()
        codeBlockLanguage = trimmed.slice(3).trim()
        inCodeBlock = true
      }
    }
    else if (inCodeBlock) {
      codeBlockLines.push(line)
    }
    else if (trimmed.startsWith('#### ')) {
      flushParagraph()
      flushList()
      const headingText = trimmed.slice(5).trim()
      elements.push(
        <h4 key={elements.length}
          className="text-base md:text-lg font-semibold text-lime-300 mb-2 md:mb-3 mt-4 md:mt-6 font-[Turret_Road]"
        >
          {parseInlineMarkdown(headingText)}
        </h4>,
      )
    }
    else if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
      flushParagraph()
      if (!inList) {
        inList = true
      }
      listItems.push(trimmed.slice(2))
    }
    else if (trimmed === '') {
      flushParagraph()
      flushList()
    }
    else {
      if (inList) {
        flushList()
      }
      currentParagraph.push(trimmed)
    }
  }

  flushParagraph()
  flushList()
  flushCodeBlock()

  return <>{elements}</>
}
